import { useEffect, useMemo, useState } from 'react';

import { searchTags } from '../services/tags';
import { getSessionKey, onSessionChange } from '../utils/session';
import {
	forgetItemTagTitles,
	itemTagTitleFor,
	rememberItemTagTitle,
	rememberItemTagTitles,
	rememberedItemTagTitles,
} from '../utils/tagTitleMemory';

/**
 * TAG SEARCH, for the pickers (docs/plans/tags.md §3.2, §5.2; keepr-api
 * § The tag page and tag search). The vocabulary never lists ITEM tags — a
 * card of 1,000 customers used as tags would be a list of 1,000 — so a
 * picker finds one by its item's title, through `GET /api/tags/search`.
 *
 * `useTagSearch(query, { enabled, collectionId, cardId, delay })` →
 * `{ results, itemTags, loading, error, more }`. `cardId` asks for one card's
 * item tags alone, and those of the cards extending it (the API's `card_id`).
 * The query settles for `delay` ms before it is asked (the route is rate
 * class `expensive`), and each answer is kept for the session, so typing back
 * over a word asks nothing.
 *
 * Every item tag a search shows is remembered for the session
 * (utils/tagTitleMemory.js), so a picked one keeps reading as its item's
 * title after the search that found it is gone.
 */

const DEFAULT_DELAY = 250;
// query|collection|card -> { owner, promise, answer }
const answers = new Map();

onSessionChange(() => { answers.clear(); });

// The title memory is utils/tagTitleMemory.js (pure, so the builders' model
// can read it); re-exported here for the components that search.
export { itemTagTitleFor, rememberItemTagTitle, rememberItemTagTitles, rememberedItemTagTitles };

/** Test seam: forget every answer and title. */
export function resetTagSearchCache() {
	answers.clear();
	forgetItemTagTitles();
}

const keyOf = (text, collectionId, cardId) => `${text.toLowerCase()}|${collectionId || ''}|${cardId || ''}`;

function ask(text, collectionId, cardId) {
	const key = keyOf(text, collectionId, cardId);
	const hit = answers.get(key);
	if (hit && hit.owner === getSessionKey()) return hit.promise;
	const entry = { owner: getSessionKey(), promise: null };
	entry.promise = searchTags(text, { collectionId, cardId })
		.then(answer => {
			(answer.results || []).forEach(result => {
				if (result?.kind === 'item' && result.tag) {
					rememberItemTagTitle(result.tag._id, {
						...result.tag,
						collection_id: result.collection?._id || result.tag.collection_id,
					});
				}
			});
			return answer;
		})
		.catch(error => {
			if (answers.get(key) === entry) answers.delete(key);
			throw error;
		});
	answers.set(key, entry);
	return entry.promise;
}

const EMPTY = Object.freeze([]);

export default function useTagSearch(query, { enabled = true, collectionId = null, cardId = null, delay = DEFAULT_DELAY } = {}) {
	const text = `${query || ''}`.replace(/\s+/g, ' ').trim();
	const active = enabled && text.length > 0;
	const [state, setState] = useState({ key: null, results: EMPTY, more: false, loading: false, error: null });

	useEffect(() => {
		if (!active) return undefined;
		const key = keyOf(text, collectionId, cardId);
		let live = true;
		setState(prev => (prev.key === key ? prev : { key, results: prev.results, more: prev.more, loading: true, error: null }));
		const timer = setTimeout(() => {
			ask(text, collectionId, cardId)
				.then(answer => { if (live) setState({ key, results: answer.results || EMPTY, more: Boolean(answer.more), loading: false, error: null }); })
				.catch(error => { if (live) setState({ key, results: EMPTY, more: false, loading: false, error }); });
		}, delay);
		return () => {
			live = false;
			clearTimeout(timer);
		};
	}, [active, text, collectionId, cardId, delay]);

	const current = active && state.key === keyOf(text, collectionId, cardId);
	const results = current ? state.results : EMPTY;
	const itemTags = useMemo(() => results.filter(result => result?.kind === 'item'), [results]);
	return {
		results,
		itemTags,
		loading: active && (!current || state.loading),
		error: current ? state.error : null,
		more: current ? state.more : false,
	};
}
